import { useContext, useState } from 'react';
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from 'reactstrap';
import styled from 'styled-components';
import cx from 'classnames';
import { PatternEditorContext } from '../contexts/PatternEditorContext';
import { MapContext } from '../contexts/MapContext';
import PatternIcon from './icons/PatternIcon';
import UnderlinedLinkButton from './basics/UnderlinedLinkButton';
import { UnstyledButton } from './basics/UnstyledButton';
import { captureEvent } from '../utils/analytics';

const MaxWidthDropdownMenu = styled(DropdownMenu)`
  max-width: calc(var(--side-panel-width) - 10px);
`;

const MaxWidthDropdownToggle = styled(DropdownToggle)`
  max-width: 150px;
`;

const DropdownItemStyles = `
  font-size: 0.8em;
  padding: 0;
  &:is(button), button {
    padding: 0.5em 1.25em;
  }
`;

const StyledDivDropdownItem = styled(({ className, children }) => (
  <div
    className={`d-flex justify-content-between align-items-center dropdown-item ${className}`}
  >
    {children}
  </div>
))`
  ${DropdownItemStyles}
`;

const StyledDropdownItem = styled(DropdownItem)`
  ${DropdownItemStyles}
`;

const BlueSvgContainer = styled.span`
  display: flex;
  align-items: center;
  svg {
    color: var(--overyield-blue);
  }
`;

const PatternListDropdown = ({
  onSelectPattern,
  title,
  selectedPatternId,
  ...buttonProps
}) => {
  const { editNewPattern, setEditorPattern, doOrConfirmIfDirty } = useContext(
    PatternEditorContext,
  );
  const {
    patterns: { inRowPatterns },
  } = useContext(MapContext);

  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => setIsOpen(!isOpen);

  return (
    <Dropdown isOpen={isOpen} toggle={toggle}>
      <MaxWidthDropdownToggle
        size="sm"
        className="d-flex align-items-center p-0 overflow-hidden"
        color="link"
        {...buttonProps}
      >
        {title || (
          <BlueSvgContainer>
            <PatternIcon className="mr-1" /> Patterns
          </BlueSvgContainer>
        )}
      </MaxWidthDropdownToggle>
      <MaxWidthDropdownMenu
        container="body"
        modifiers={{ preventOverflow: { boundariesElement: 'viewport' } }}
      >
        {inRowPatterns.length > 0 && (
          <>
            {inRowPatterns.map((pattern) => (
              <StyledDivDropdownItem key={pattern.id}>
                <UnstyledButton
                  title={pattern.name}
                  onClick={() => {
                    onSelectPattern(pattern);
                    toggle();
                  }}
                  className={cx(
                    'text-left flex-grow-1 flex-shrink-1 text-truncate',
                    {
                      'font-weight-bold': selectedPatternId === pattern.id,
                    },
                  )}
                >
                  {pattern.name}
                </UnstyledButton>
                <UnderlinedLinkButton
                  $hoverOnly
                  color="link"
                  size="sm"
                  className="font-small text-muted"
                  onClick={(e) => {
                    captureEvent('pattern-viewed-existing');
                    e.preventDefault();
                    doOrConfirmIfDirty(() => {
                      setEditorPattern(pattern);
                    });
                  }}
                >
                  view
                </UnderlinedLinkButton>
              </StyledDivDropdownItem>
            ))}
            <DropdownItem divider />
          </>
        )}
        <StyledDropdownItem
          onClick={() => {
            captureEvent('pattern-clicked-new');
            doOrConfirmIfDirty(editNewPattern);
          }}
          color="link"
          size="sm"
        >
          <div>＋ New in-row pattern</div>
        </StyledDropdownItem>
      </MaxWidthDropdownMenu>
    </Dropdown>
  );
};

export default PatternListDropdown;
